<template>
  <div class="product-item">
    <div>
      <!-- 点击时再写 cache：避免 mouseenter 在推荐列表上刷大量 set-params -->
      <a :href="hrefInfo.url" target="_blank" @click="handleSetCookie" @auxclick="handleSetCookie">
        <div class="product-image">
          <client-only>
            <img v-if="isLazy" v-lazy="$cvWebpFn(productData.image)" :alt="productData.title || ''" />

            <img v-else :src="$cvWebpFn(productData.image)" :alt="productData.title" />
            <template #fallback>
              <img v-lazy="useImg1pxBase64().value" lazy="loading" />
            </template>
          </client-only>
          

          <template v-if="isAds">
            <div class="ads-mark">Sponsored</div>
          </template>
        </div>

        <div class="info-box">
          <p v-if="type !== 'whats-new'" class="product-title">
            <img
              v-if="showChoiceTag"
              class="choice-tag"
              :src="$cvWebpFn(CHOICE_BADGE_URL)"
              alt="Kilimall Choice"
            />
            <img
              v-if="showNewTag"
              class="new-tag"
              :src="$cvWebpFn(NEW_BADGE_URL)"
              alt="New"
            />
            <img
              v-if="showFlashSaleTag"
              class="flash-sale-tag"
              :src="$cvWebpFn(FLASH_SALE_BADGE_URL)"
              alt="Flash Sale"
            />
            <img
              v-for="badge in promoBadges"
              :key="badge.key"
              class="promo-tag"
              :src="$cvWebpFn(badge.url)"
              :alt="badge.alt"
            />
            {{ productData.title }}
          </p>
          <div class="product-price" :style="{ textAlign: type === 'whats-new' ? 'center' : 'left' }">
            {{ $currencySymbol() }} {{ $toThousandsFn(previewPrice) }}
          </div>
          <div v-if="type === 'flash-sale' && productData.salePrice > 0" class="del-price">
            {{ $currencySymbol() }} {{ $toThousandsFn(productData.salePrice) }}
          </div>
          <div class="rate" v-if="type === 'normal'">
            <van-rate size="14" color="#F9BE00" :model-value="productData.reviewStar || 5" />
            <span class="reviews" v-if="'reviews' in productData">({{ productData.reviews }})</span>
          </div>

          <div class="mark-box">
            <img
              v-if="showBrandOfficial"
              class="store-tag"
              :src="$cvWebpFn(OFFICIAL_STORE_BADGE_URL)"
              alt="Brand Official"
            />
            <img
              v-if="showBrandAuthorized"
              class="store-tag"
              :src="$cvWebpFn(AUTHORIZED_STORE_BADGE_URL)"
              alt="Brand Authorized"
            />
            <img
              v-if="showPreferred"
              class="store-tag"
              :src="$cvWebpFn(PREFERRED_STORE_BADGE_URL)"
              alt="Preferred Store"
            />
            <template v-for="(tag, idx) in productData.logisticsTypes" :key="idx">
              <img
                v-if="getLogisticsBadgeUrl(tag.name)"
                class="logistics-tag"
                :src="$cvWebpFn(getLogisticsBadgeUrl(tag.name))"
                :alt="tag.name"
              />
              <div v-else class="logistics-tag logistics-tag--text">
                <span class="tag-name" :style="{ color: tag.color }">{{ tag.name }}</span>
                <span class="tag-bg" :style="{ background: tag.color, opacity: 0.2 }"></span>
              </div>
            </template>
          </div>

          <div class="stock-process-bar" v-if="type === 'flash-sale' && 'inventory' in props.productData">
            <span v-if="showStockProcess" class="process-val">{{ Math.ceil(stockProcess) }}% Left</span>
            <span class="process-val process-val-sold" v-else>
              <span v-if="status === 1" style="color: #DD3131;">Sold Out</span>
              <span v-else-if="status === 4">Over</span>
              <span v-else="status === 3" style="color: #fff;">Coming Up</span>
            </span>
            <div class="inner-bar" :style="{ width: `${stockProcess}%` }"></div>
          </div>
        </div>
      </a>
    </div>
    <div v-if="type === 'favorite'" class="favorite-tool-bar">
      <span @click.stop="handleDelWishClick" class="tool-item"><van-icon name="delete" size="20" /></span>
    </div>

    <div v-if="showAddCart" class="add-cart-btn" @click.stop="handleAddCartClick">
      <van-icon name="shopping-cart-o" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { delWishlist, setUrlCache } from '@/api/models/system'
import { showConfirmDialog } from 'vant'
import {
  buildFlashSaleListingSource,
  buildProductDetailStoreSpecifiedSource,
  buildWhatsNewRecommendSource,
  FLASH_SALE_BADGE_URL,
  getProductTrafficSourceTracking,
  parseTrafficSourceTrackingToListingQuery,
  RECENT_VIEW_LATEST_TRAFFIC_SOURCE_PAGE,
  resolveIsFlashSaleTag,
  resolvePromoBadges,
  type IToListingQuery
} from '~/plugins/utils';

const props = withDefaults(defineProps<{ 
  productData: any,
  isAds?: boolean,
  isLazy?: boolean,
  showAddCart?: boolean
  /**
   * flash-sale 秒杀
   * whats-new 新品
   * normal 普通
   * favorite 收藏
   * default 默认（图片，价格 和 标题）
   */
  type?: 'flash-sale' | 'whats-new' | 'normal' | 'favorite' | 'default' | 'recent-view'
  status?: number
  flashSaleSessionLabel?: string
  flashSaleCategoryName?: string
  /** 秒杀进商详 source 的 firstPage，默认 home */
  flashSaleFirstPage?: string
  /** 秒杀进商详 source 的 latestPage，默认 flashSale；首页可传 home */
  flashSaleLatestPage?: string
}>(), {
  type: 'normal',
  status: -1,
  isLazy: true
})

interface IEmits {
  (e: 'del:ok', item: any): void
  (e: 'add-cart', item: any): void
  // (e: 'add-cart:ok', item: any): void
}
const emits = defineEmits<IEmits>()

const CHOICE_BADGE_URL = 'https://image.kilimall.com/c/h5/KiliChoice.png'
const NEW_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_new_goods.png'
const OFFICIAL_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_official_store.png'
const AUTHORIZED_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_authorized_store.png'
const PREFERRED_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_preferred_storex.png'
const LOGISTICS_BADGE_URLS: Record<string, string> = {
  'local dispatch': 'https://image.kilimall.com/c/h5/ic_local_dispatch.png',
  fbk: 'https://image.kilimall.com/c/h5/ic_fbk.png',
  'fulfilled by kilimall': 'https://image.kilimall.com/c/h5/ic_fbk.png',
  'oversea shipping': 'https://image.kilimall.com/c/h5/ic_overseas_shipping.png',
  'oversea shipment': 'https://image.kilimall.com/c/h5/ic_overseas_shipping.png'
}

const route = useRoute()
const { $getListingDetailHref } = useNuxtApp()
const previewPrice = computed(() => {
  const { activityPrice, listPrice, minPrice, price } = props.productData
  
  return activityPrice || listPrice || minPrice || price
})

const stockProcess = computed(() => {
  if(!props.productData?.inventory || !props.productData?.promiseStock) {
    return 0
  }
  
  return (props.productData.inventory / props.productData.promiseStock) * 100
})

const showStockProcess = computed(() =>{
  const isShow = props.type === 'flash-sale' &&
    props.status === 1 &&
    props.productData?.inventory &&
    props.productData?.promiseStock
  return isShow
})

const showBrandOfficial = computed(() => {
  // storeAuthType: 1 官方店铺
  return props.productData?.storeAuthType === 1
})
const showBrandAuthorized = computed(() => {
  // storeAuthType: 2 品牌授权
  return props.productData?.storeAuthType === 2
})

const showPreferred = computed(() => props.productData?.tags?.includes('store_preferred'))

const getLogisticsBadgeUrl = (name?: string) =>
  LOGISTICS_BADGE_URLS[name?.trim().toLowerCase() || '']

const showNewTag = computed(() => {
  return Array.isArray(props.productData?.highlights) && props.productData.highlights.includes('NEW')
})

const showChoiceTag = computed(() => {
  return Array.isArray(props.productData?.highlights)
    && props.productData.highlights.some((item: string) => item.includes('Choice'))
})

const showFlashSaleTag = computed(() =>
  resolveIsFlashSaleTag(props.productData?.highlights)
)

const promoBadges = computed(() =>
  resolvePromoBadges(props.productData?.highlights)
)

const handleSetCookie = () => {
  const { cookie, key } = hrefInfo.value
  if (!key || !cookie) return
  setUrlCache(cookie, key).catch((e) => {
    console.error('setUrlCache failed:', e)
  })
}

const hrefFn = ()=> {
  const { source: sourceQuery } = route.query
  // 其他类型的来源
  const {
      insideLatestTrafficSourceValue: sourceValue = '',
      internalLatestTrafficSourceModule: sourceModule = '',
      internalLatestTrafficSourceType: sourceType = '',
      internalFirstTrafficSourcePage: firstPage = '',
      internalLatestTrafficSourcePage: latestPage = ''
    } = props.productData

  let { source = '' } = useRoute().query
  if(!sourceQuery && (sourceValue || sourceModule || sourceType)) {
    // 收藏列表与 WAP wish-list 对齐：保留 firstPage，latest 固定 wishList
    const resolvedLatestPage = props.type === 'favorite' ? 'wishList' : (latestPage || '')
    source = `${sourceModule}|${sourceType}|${sourceValue}|${firstPage || ''}|${resolvedLatestPage}`
  }
  if (props.productData.source) {
    // 商品信息中的来源
    source = props.productData.source
  }
  if (props.type === 'whats-new') {
    source = props.productData.source || buildWhatsNewRecommendSource(
      props.productData.modelName || props.productData.algorithmModelName || ''
    )
  }
  if (props.type === 'store-selective') {
    source = buildProductDetailStoreSpecifiedSource()
  }

  const recentViewTrackingQuery = props.type === 'recent-view'
    ? parseTrafficSourceTrackingToListingQuery(
      getProductTrafficSourceTracking(props.productData),
      { latestTrafficSourcePage: RECENT_VIEW_LATEST_TRAFFIC_SOURCE_PAGE }
    )
    : {}
  
  let url
  if(props.type === 'flash-sale') {
    // 跳转到秒杀详情页
    const { skuId, title, shortTitle /** image, activityId, id, */  } = props.productData

    const toDetailParams: IToListingQuery = {
      title,
      shortTitle,
      isAd: props.isAds ? 'ad' : undefined,
      isNewTag: showNewTag.value,
      isChoiceTag: showChoiceTag.value,
      requestId: props.productData.requestId || '',
      algorithmModelName: props.productData.algorithmModelName || '',
      source: buildFlashSaleListingSource(
        props.flashSaleSessionLabel || '',
        props.flashSaleCategoryName || '',
        props.flashSaleFirstPage || 'home',
        undefined,
        props.flashSaleLatestPage || 'flashSale'
      )
    }

    if(props.status === 1) {
      toDetailParams.from = 'flash-sale'
    } else {
      toDetailParams.type = 'sku'
    }

    url = $getListingDetailHref(skuId, toDetailParams, { writeCache: false })
  } else {
    const { title, listingId, source: _dataSource, defaultSkuId, shortTitle, skuId, /** image */ } = props.productData
    const toDetailParams: IToListingQuery = {
      title,
      shortTitle,
      isAd: props.isAds ? 'ad' : undefined,
      isNewTag: showNewTag.value,
      isChoiceTag: showChoiceTag.value,
      requestId: props.productData.requestId || '',
      algorithmModelName: props.productData.algorithmModelName || '',
      source: source || _dataSource,
      ...recentViewTrackingQuery
    }

    const flashSkuId = Number(defaultSkuId || skuId || 0)
    if (showFlashSaleTag.value && flashSkuId > 0) {
      toDetailParams.from = 'flash-sale'
      url = $getListingDetailHref(flashSkuId, toDetailParams, { writeCache: false })
    } else {
      toDetailParams.skuId = defaultSkuId
      url = $getListingDetailHref(listingId, toDetailParams, { writeCache: false })
    }
  }
  return url
}
const hrefInfo = computed(hrefFn)

const handleAddCartClick = () => {
  emits('add-cart', props.productData)
}
const handleDelWishClick = async() => {
  await showConfirmDialog({
    title: 'Delete',
    message: 'Are you sure?',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    cancelButtonColor: 'red'
  })

  await delWishlist({ listingId: props.productData.listingId })

  emits('del:ok', props.productData)
}
</script>

<style lang="scss" scoped>
.product-item {
  width: 100%;
  height: 100%;
  color: var(--font_color_title);
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  padding: 20px;
  transition: all .15s;
  position: relative;
  cursor: pointer;
}
.add-cart-btn{
  position: absolute;
  top: 500px;
  right: 10px;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background-color: rgba($color: #DD3131, $alpha: 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  i{
    font-size: 40px;
    color: var(--main_color);
  }
}
.product-item .product-image {
  width: 100%;
  height: 0;
  overflow: hidden;
  padding-top: 100%;
  position: relative;
  .discount{
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    font-size: 26px;
    color: #fff;
    min-width: 80px;
    padding: 0 10px;
    background-image: url(https://image.kilimall.com/kenya/PcImages/flash_discount.png.webp);
    background-size: 100% 100%;
    height: 80px;
    text-align: center;
    line-height: 70px;
  }
  .ads-mark{
    position: absolute;
    color: #F7F7F7;
    font-size: 22px;
    padding: 8px;
    background: black;
    border-radius: 4px 0 0 0;
    right: 0px;
    bottom: 0px;
    opacity: 20%;
  }
  img {
    width: 100%!important;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    -o-object-fit: cover;
    object-fit: cover;
  }
}
.product-item .info-box{
  .product-title{
    margin: 10px 0;
    line-height: 32px;
    height: 65px;
    @include ellipsisMultiline(2);
    .choice-tag {
      display: inline-block;
      height: 30px;
      width: auto;
      margin-right: 10px;
      margin-top: -2px;
      vertical-align: middle;
    }
    .flash-sale-tag,
    .promo-tag {
      display: inline-block;
      height: 30px;
      width: auto;
      margin-right: 10px;
      margin-top: -2px;
      vertical-align: middle;
    }
    .new-tag {
      display: inline-block;
      width: auto;
      height: 30px;
      margin-right: 10px;
      vertical-align: middle;
      transform: translateY(-2px);
    }
  }
  .product-price{
    font-size: 34px;
    font-weight: 600;
    margin-top: 20px;
    color: var(--pc-default-color);
  }
  .del-price{
    color: #666;
    text-decoration: line-through;
    font-size: 26px;
  }
}
.rate {
  width: 100%;
  display: flex;
  margin-top: 20px;
  align-items: center;
  color: #999999;
  .reviews{
    font-size: 22px;
    display: inline-block;
    margin-left: 10px;
  }
}

.mark-box{
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.store-tag {
  display: block;
  width: auto;
  height: 30px;
  margin-top: 20px;
}
.logistics-tag{
  display: block;
  width: auto;
  height: 30px;
  margin-top: 20px;
  &--text {
    overflow: hidden;
    position: relative;
    width: auto;
    height: auto;
    border-radius: 6px;
    padding: 2px 8px;
    color: #fff;
    font-size: 22px;
    .tag-name{
      position: relative;
      z-index: 1;
      white-space: nowrap;
    }
    .tag-bg{
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}

$barHeight: 44px;
.stock-process-bar{
  background-color: #FFBEBD; // #FFE6E8;
  border-radius: $barHeight;
  height: $barHeight;
  width: 100%;
  overflow: hidden;
  position: relative;
  margin: 20px 0;
  .process-val{
    position: absolute;
    left: 0;
    right: 0;
    left: 0;
    bottom: 0;
    height: $barHeight;
    color: #fff;
    font-size: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .process-val-sold{
    color: var(--pc-default-color);
    font-weight: 600;
  }
  .inner-bar{
    width: 80%;
    height: 100%;
    border-radius: $barHeight;
    background: linear-gradient(to right, #ff582b, #ff2642);
  }
}
.favorite-tool-bar{
  position: absolute;
  opacity: 0;
  top: 30%;
  left: calc(50% - 50px);
  @include center();
  .tool-item{
    display: inline-block;
    border-radius: 50%;
    // padding: 30px;
    @include center();
    width: 100px;
    height: 100px;
    background-color: rgba($color: #fff, $alpha: .6);
    transition: all .15s;
    &:hover{
      color: #fff;
      background-color: rgba($color: #DD3131, $alpha: .6);
    }
  }
}
.product-item:hover{
  box-shadow: 4px 6px 20px #DADADA;
  transform: scale(1.04);
  z-index: 9;
  .product-title{
    color: var(--pc-default-color);
  }
  .favorite-tool-bar{
    opacity: 1;
  }
}
</style>
